import { useEffect, useMemo, useState } from "react";
import { BackupBar } from "@/components/BackupBar";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useStored } from "./tripStore";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { COMMON_ESSENTIALS, PRESETS, PACKING_LIST_KEY, buildPackingSnapshot, restorePackingEntries } from "./packingBackup";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Generates a packing checklist based on trip type — beach, business, camping, international, or winter — with common essentials included automatically, plus room to add your own items. You can also create your own named trip type from scratch for a kind of trip not covered by the built-in presets.`,
  how: `Select your trip type to load a relevant starting checklist, check items off as you pack them, and add any custom items specific to your trip. Use "New Trip Type" to create your own named preset (like "Ski Trip" or "Wedding Guest") and build its item list from scratch — it's saved on this device for next time.`,
  tip: `Pack and check off your list a day or two before departure rather than the morning of — it leaves time to actually buy anything you discover you're missing.`,
};

export default function TravelPackingList() {
  const [customTripTypes, setCustomTripTypes] = useStored<Record<string, string[]>>("fdp-packing-custom-types-v1", {});
  const [tripTypes, setTripTypes] = useStored<string[]>("fdp-packing-types-v1", ["Beach"]);
  const [checkedList, setCheckedList, resetChecked] = useStored<string[]>("fdp-packing-checked-v1", []);
  const [customItems, setCustomItems] = useStored<string[]>("fdp-packing-extra-v1", []);
  const [newItem, setNewItem] = useState("");
  const [newTypeName, setNewTypeName] = useState("");
  const [creatingType, setCreatingType] = useState(false);
  const checked = useMemo(() => new Set(checkedList), [checkedList]);
  // The single selected type, if exactly one is selected and it is user-made: new items are saved into it.
  const tripType = tripTypes.length === 1 ? tripTypes[0] : "";
  const setTripType = (t: string) => setTripTypes([t]);
  const toggleType = (t: string) => setTripTypes(tripTypes.includes(t) ? (tripTypes.length > 1 ? tripTypes.filter((x) => x !== t) : tripTypes) : [...tripTypes, t]);

  // Mirror the whole list (types + items + ticks) so a backup always carries it, even before anything was edited.
  useEffect(() => {
    try { sessionStorage.setItem(PACKING_LIST_KEY, JSON.stringify(buildPackingSnapshot(tripTypes, customTripTypes, customItems, checkedList))); } catch { /* storage blocked */ }
  }, [tripTypes, customTripTypes, customItems, checkedList]);

  const isCustomType = tripType !== "" && tripType in customTripTypes;
  const basePresetItems = tripTypes.flatMap((t) => customTripTypes[t] ?? PRESETS[t] ?? []);

  const items = useMemo(() => {
    const seen = new Set<string>();
    return [...COMMON_ESSENTIALS, ...basePresetItems, ...customItems].filter((i) => { const k = i.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  }, [basePresetItems, customItems]);

  const createTripType = () => {
    const name = newTypeName.trim();
    if (!name) return;
    if (PRESETS[name] || customTripTypes[name]) {
      toast.error("A trip type with that name already exists.");
      return;
    }
    setCustomTripTypes(prev => ({ ...prev, [name]: [] }));
    setTripType(name);
    setNewTypeName("");
    setCreatingType(false);
    toast.success(`Created "${name}" — add items below to build its list.`);
  };

  const deleteTripType = (name: string) => {
    setCustomTripTypes(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    if (tripTypes.includes(name)) setTripTypes(tripTypes.filter((t) => t !== name).length ? tripTypes.filter((t) => t !== name) : ["Beach"]);
    toast.success(`Deleted "${name}".`);
  };

  const toggle = (item: string) => {
    setCheckedList(prev => (prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]));
  };

  const addCustom = () => {
    if (!newItem.trim()) return;
    const dup = items.some(i => i.toLowerCase() === newItem.trim().toLowerCase());
    if (dup) { toast.info("That item is already on the list."); setNewItem(""); return; }
    if (isCustomType) {
      // For a user-created trip type, added items become part of that
      // type's own saved list (so it's still there next time this trip
      // type is selected), rather than the ephemeral per-session list
      // used for built-in presets.
      setCustomTripTypes(prev => ({ ...prev, [tripType]: [...prev[tripType], newItem.trim()] }));
    } else {
      setCustomItems([...customItems, newItem.trim()]);
    }
    setNewItem("");
  };

  const removeCustom = (item: string) => {
    const owner = Object.keys(customTripTypes).find(t => tripTypes.includes(t) && customTripTypes[t].includes(item));
    if (owner) {
      setCustomTripTypes(prev => ({ ...prev, [owner]: prev[owner].filter(i => i !== item) }));
    } else {
      setCustomItems(customItems.filter(i => i !== item));
    }
  };

  const { logUsageNow } = useLogUsageOnAction();

  // Was useLogToolUsage(creatingType, undefined, { ownActions: true }) -- unrelated to whether there's
  // a real result to deliver. copyList (the actual deliverable
  // action) is the real gate now.
  const copyList = async () => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    const text = items
      .map(i => `${checked.has(i) ? "[x]" : "[ ]"} ${i}`)
      .join("\n");
    void copyText(text, "Packing list copied");
  };


  return (
    <ToolContainer>
      <div className="flex flex-wrap gap-2 mb-2">
        {Object.keys(PRESETS).map(type => (
          <Button
            key={type}
            size="sm"
            variant={tripTypes.includes(type) ? "default" : "outline"}
            aria-pressed={tripTypes.includes(type)}
            onClick={() => toggleType(type)}
          >
            {type}
          </Button>
        ))}
        {Object.keys(customTripTypes).map(type => (
          <div key={type} className="relative">
            <Button
              size="sm"
              variant={tripTypes.includes(type) ? "default" : "outline"}
              aria-pressed={tripTypes.includes(type)}
              onClick={() => toggleType(type)}
              className="pr-7"
            >
              {type}
            </Button>
            <button
              onClick={() => deleteTripType(type)}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-red-500"
              title={`Delete "${type}"`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      {creatingType ? (
        <div className="flex gap-2 mb-6">
          <Input
            value={newTypeName}
            onChange={e => setNewTypeName(e.target.value)}
            placeholder="Name your trip type, e.g. Ski Trip"
            onKeyDown={e => e.key === "Enter" && createTripType()}
            autoFocus
          />
          <Button onClick={createTripType}>Create</Button>
          <Button variant="ghost" onClick={() => { setCreatingType(false); setNewTypeName(""); }}>Cancel</Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="gap-1.5 mb-6" onClick={() => setCreatingType(true)}>
          <Plus className="h-3.5 w-3.5" /> New Trip Type
        </Button>
      )}

      <div className="flex gap-2 mb-4">
        <Input
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          placeholder="Add a custom item..."
          onKeyDown={e => e.key === "Enter" && addCustom()}
        />
        <Button onClick={addCustom}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-1">
        {items.map(item => (
          <div
            key={item}
            className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50"
          >
            <input
              type="checkbox"
              checked={checked.has(item)}
              onChange={() => toggle(item)}
            />
            <span
              className={
                checked.has(item) ? "line-through text-muted-foreground" : ""
              }
            >
              {item}
            </span>
            {(customItems.includes(item) || Object.keys(customTripTypes).some(t => tripTypes.includes(t) && customTripTypes[t].includes(item))) && (
              <button
                onClick={() => removeCustom(item)}
                className="ml-auto text-muted-foreground hover:text-red-500"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="text-sm text-muted-foreground mt-4">
        {checked.size} of {items.length} packed
      </p>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" onClick={copyList}>Copy List</Button>
        <Button variant="ghost" onClick={resetChecked}>Untick all</Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">Pick one or more trip types. Ticks and your own items are kept for this visit only.</p>
          <BackupBar tool="travel-packing-list" prefixes={["fdp-trip-", "fdp-packing-"]} session transformRestore={restorePackingEntries} />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
